'use client'

import { motion } from 'framer-motion'
import { Container } from '@/shared/components/Container'
import Counter from '@/shared/components/framer/Counter'
import React, { ReactNode } from 'react'
import Image from 'next/image'
import paperTearBlueImage from '@/images/localized/paper-tear-sm-blue.png'
import paperTearImage from '@/images/localized/paper-tear-sm.webp'

import { CloudBackground } from '@/shared/components/CloudBackground'
import SkipTheWaitCalculator from '@/shared/components/interactive/SkipTheWaitCalculator'
import { Footer } from '@/shared/components/footer/Footer'
import ScrollSectionNsw from '@/shared/components/interactive/ScrollSectionNsw'
import SlideUp from '@/shared/components/interactive/SlideUp'
import {
  AustraliaIcon,
  EarthIcon,
  SnorkelIcon,
  TripIcon,
  TrophyIcon,
  WalkIcon,
} from '@/shared/components/icons/noun/NounIcons'
import ConsultationStatisticsCardNsw from '@/shared/components/interactive/ConsultationStatisticsNsw'
import ServicesAdWithPaperTear from '@/shared/components/ServicesAdWithPaperTear'
import ClinicStatisticsHeaderNsw from '@/shared/components/interactive/ClinicStatisticsHeaderNsw'
import { LocalizedDoctorsLandingPage } from '@/lib/apis/sanity'
import ReviewSection from '@/shared/components/ReviewSection'

type Props = {
  content: LocalizedDoctorsLandingPage
}

export default function NswPage({ content }: Props) {
  return (
    <>
      <div className="relative pb-16">
        <div className="absolute -bottom-16 left-0 z-[0] w-full">
          <CloudBackground />
        </div>
        <ClinicStatisticsHeaderNsw />
        <ConsultationStatisticsCardNsw />
      </div>
      <ServicesAdWithPaperTear />
      <NumbersSection />
      <QuoteSection />
      <ReviewSection content={content.reviewSection} />
      <SkipTheWaitCalculator
        tiers={[
          [
            'Watch an episode of "Succession", or any other TV show - you have plenty of options',
            'Complete a workout session in the gym, and get that sweet endorphin boost',
            'Take your dog for a long walk at the park and train some tricks',
            'Water the plants in your garden and do some re-potting',
            "Go for that romantic dinner at the restaurant you've always wanted to try",
          ],
          [
            "Finally catch that big game you've been waiting for – jersey and snacks ready!",
            'Treat yourself to that overdue massage or facial',
            'Roll up your sleeves and master the art of sushi-making – impress your friends at your next dinner party',
            "Hit some balls at the driving range and work on that swing you've been dreaming about",
            "Try cooking that recipe you saved on Instagram months ago, it's time to brush up on your culinary skills",
          ],
          [
            'Finally catch those waves at Bondi and learn to surf like a local',
            "Start that home DIY project you've been pinning on Pinterest forever",
            'Surprise your family with a fancy home-cooked feast (MasterChef style!)',
            'Pack your favorite snacks and go on that Royal National Park adventure you keep talking about',
            'Get your hands messy and discover your inner artist at a pottery class',
          ],
          [
            'Finally finish that book sitting on your bedside table (without falling asleep!)',
            "Settle in for the ultimate binge-watch of that show everyone's talking about",
            "Escape the city and explore those misty Blue Mountains trails you've been eyeing",
            "Learn some basic photography skills and become the designated photographer in your friend's group",
            "Learn the car maintenance tricks that'll make your mechanic proud (and save your wallet!)",
          ],
          [
            'Hit the road for an epic Sydney to Brisbane beach-hopping adventure.',
            'Grab some snacks and dive into a Harry Potter movie marathon.',
            'Level up your coffee game with some proper barista training.',
            'Pick up a guitar and nail those favorite tunes of yours.',
            'Get your hands dirty and grow your own backyard veggie patch.',
          ],
          [
            "Cruise that coastal route 'til you know every beach stop between Sydney and Brissy.",
            'Make those Harry Potter marathons your weekend tradition - wand optional!',
            'Start with barista basics, nail the perfect brew, then rock your own coffee shop.',
            'Go from strumming basics to crushing those guitar solos on your favorite tracks.',
            'Turn your green thumb into a superpower with multiple thriving veggie gardens.',
          ],
        ]}
      />
      <Footer />
    </>
  )
}

function QuoteSection() {
  return (
    <div className="bg-white">
      <Container className="my-32">
        <p className="mx-auto max-w-4xl text-center font-sans text-xl font-light leading-relaxed text-gray-700 xl:text-3xl">
          {'"'}
          There{"'"}s a Better Way At NextClinic, we believe your time matters.
          That{"'"}s why we{"'"}re revolutionizing GP accessibility through
          telehealth services.
          {'"'}
        </p>
      </Container>
    </div>
  )
}

function NumbersSection() {
  return (
    <div className="relative bg-gradient-to-b from-blue-200 to-blue-50">
      <div className="absolute left-0 top-0 h-full w-full overflow-hidden">
        <div className="h-full w-full bg-[url('/images/grid-lines.svg')] bg-cover bg-repeat opacity-20 xl:h-[250%] xl:w-[250%]" />
      </div>
      <div className="absolute left-0 top-0 w-full">
        <Image
          src={paperTearBlueImage}
          alt="paper tear image background"
          className="h-auto w-full"
        />
      </div>
      <div className="relative">
        <div className="absolute bottom-0 left-0 w-full scale-y-[-1]">
          <Image
            src={paperTearImage}
            alt="paper tear image background"
            className="h-auto w-full"
          />
        </div>
        <Container className="relative mt-40 xl:mt-64">
          <ScrollSectionNsw />
          <div className="relative mt-32 grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 xl:mt-40 xl:grid-cols-3">
            {[
              {
                value: 825,
                suffix: 'M',
                description: 'Repeats of the song “Down Under”',
                icon: <AustraliaIcon />,
              },
              {
                value: 75,
                suffix: 'M',
                description: 'Round trips from Opera House to Bondi Beach',
                icon: <TripIcon />,
              },
              {
                value: 900,
                suffix: 'K',
                description: 'Walks along the Great Ocean Road',
                icon: <WalkIcon />,
              },
              {
                value: 130,
                suffix: 'K',
                description: 'Rewatch of every AFL Grand Final ever since 1897',
                icon: <TrophyIcon />,
              },
              {
                value: 65,
                suffix: 'K',
                description:
                  'Swims across the length of the Great Barrier Reef',
                icon: <SnorkelIcon />,
              },
              {
                value: 360,
                description: 'Circles around the entire planet on foot',
                icon: <EarthIcon />,
              },
            ].map(({ icon, ...metrics }, idx) => (
              <MetricsCard key={idx} {...metrics}>
                <div className="absolute left-1/2 top-1/2 h-full w-full -translate-x-1/4 -translate-y-1/2 opacity-5">
                  {icon}
                </div>
              </MetricsCard>
            ))}
          </div>
        </Container>
      </div>
    </div>
  )
}

type MetricsCardProps = {
  value: number
  suffix?: string
  description: string
  children?: ReactNode
}

function MetricsCard({
  value,
  suffix,
  description,
  children,
}: MetricsCardProps) {
  return (
    <motion.div
      className="relative overflow-hidden rounded-xl border border-gray-300 bg-gray-100 px-8 py-6 shadow-lg"
      initial="start"
      whileInView="end"
      viewport={{ once: true, amount: 'all' }}
    >
      {children}
      <SlideUp className="relative">
        <p className="font-display text-9xl font-light text-rose-500 xl:text-9xl">
          <span>
            <Counter value={value} stiffness={600} />
          </span>
          {suffix && (
            <span className="text-6xl font-bold xl:text-6xl"> {suffix}</span>
          )}
        </p>
      </SlideUp>
      <motion.p
        className="relative mt-4 font-sans text-lg text-gray-700"
        variants={{
          start: {
            filter: 'blur(10px)',
          },
          end: {
            filter: 'blur(0px)',
          },
        }}
        transition={{ ease: 'easeOut', duration: 0.5 }}
      >
        {description}
      </motion.p>
    </motion.div>
  )
}
